import axios from 'axios'
import { getChatToken } from '../../scripts/chat.auth'
import { attachRetryInterceptor, attachRefreshInterceptor } from '../../scripts/axios.resilience'

const chatApi = axios.create({
  baseURL: process.env.REACT_APP_CHAT_API_URL || 'https://chat-api.digitaladmin.io'
})

chatApi.interceptors.request.use(config => {
  config.headers.Authorization = `Bearer ${getChatToken()}`
  return config
})

attachRetryInterceptor(chatApi)
attachRefreshInterceptor(chatApi)

export interface ChatConversation {
  id: string;
  company_id: string;
  is_group: boolean;
  participants: ChatParticipant[];
  last_message?: ChatMessage | null;
  unread_count: number;
  created_at: string;
  updated_at: string;
}

export interface ChatParticipant {
  user_id: string;
  display_name: string;
  role: string | null;
  joined_at: string | null;
  left_at: string | null;
  acting_as_company_id: string | null;
  acting_as_company_name: string | null;
  acting_as_company_uuid: string | null;
}

export interface ChatMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  created_at: string;
  read_by?: string[];
}

export interface CompanyMember {
  user_id: string;
  first_name: string;
  last_name: string;
  avatar: string | null;
  email: string;
  roles: string[];
}

// --- Normalizers (_id → id) ---

function normalizeParticipant(raw: any): ChatParticipant {
  return {
    user_id: raw.user_id ?? raw._id ?? '',
    display_name: raw.display_name ?? '',
    role: raw.role ?? null,
    joined_at: raw.joined_at ?? null,
    left_at: raw.left_at ?? null,
    acting_as_company_id: raw.acting_as_company_id ?? null,
    acting_as_company_name: raw.acting_as_company_name ?? null,
    acting_as_company_uuid: raw.acting_as_company_uuid ?? null,
  }
}

function normalizeMessage(raw: any): ChatMessage {
  return {
    id: raw.id ?? raw._id ?? '',
    conversation_id: raw.conversation_id ?? '',
    sender_id: raw.sender_user_id ?? raw.sender_id ?? '',
    content: raw.content ?? '',
    created_at: raw.created_at ?? '',
    read_by: raw.read_by,
  }
}

function normalizeConversation(raw: any): ChatConversation {
  const participants = Array.isArray(raw.participants)
    ? raw.participants.map(normalizeParticipant)
    : []
  const lastMsg = raw.last_message ? normalizeMessage(raw.last_message) : null
  return {
    id: raw.id ?? raw._id ?? '',
    company_id: raw.company_id ?? '',
    is_group: raw.is_group ?? false,
    participants,
    last_message: lastMsg,
    unread_count: raw.unread_count ?? 0,
    created_at: raw.created_at ?? '',
    updated_at: raw.updated_at ?? '',
  }
}

function normalizeMember(raw: any): CompanyMember {
  return {
    user_id: raw.user_id ?? raw._id ?? '',
    first_name: raw.first_name ?? '',
    last_name: raw.last_name ?? '',
    avatar: raw.avatar ?? null,
    email: raw.email ?? '',
    roles: raw.roles ?? [],
  }
}

// --- Conversations ---

export async function getConversations(): Promise<ChatConversation[]> {
  const res = await chatApi.get('/conversations')
  const data = res.data?.data ?? res.data
  const items = data?.items ?? data ?? []
  return Array.isArray(items) ? items.map(normalizeConversation) : []
}

export async function getConversation(conversationId: string): Promise<ChatConversation> {
  const res = await chatApi.get(`/conversations/${conversationId}`)
  return normalizeConversation(res.data?.data ?? res.data)
}

export async function findOrCreateConversation(
  participantUserId: string,
  actingAsCompanyId: string | null,
): Promise<ChatConversation> {
  const res = await chatApi.post('/conversations', {
    is_group: false,
    acting_as_company_id: actingAsCompanyId ?? null,
    participants: [{ user_id: participantUserId }],
  })
  return normalizeConversation(res.data?.data ?? res.data)
}

// --- Messages ---

export async function getMessages(conversationId: string, page = 1, limit = 50): Promise<{ items: ChatMessage[]; pagination?: any }> {
  const res = await chatApi.get(`/conversations/${conversationId}/messages`, { params: { page, limit } })
  const data = res.data?.data ?? res.data
  const items = data?.items ?? data ?? []
  // Diagnostic: log raw first message for the report — remove after validation
  if (Array.isArray(items) && items.length > 0) {
    console.log('[chat debug] raw message[0]=', JSON.stringify(items[0]))
  }
  return { items: Array.isArray(items) ? items.map(normalizeMessage) : [], pagination: data?.pagination }
}

export async function postMessage(conversationId: string, content: string): Promise<ChatMessage> {
  const res = await chatApi.post(`/conversations/${conversationId}/messages`, { content })
  return normalizeMessage(res.data?.data ?? res.data)
}

export async function markRead(conversationId: string): Promise<void> {
  await chatApi.put(`/conversations/${conversationId}/messages/read`)
}

// --- Members ---

export async function getCompanyMembers(companyId: string): Promise<CompanyMember[]> {
  const res = await chatApi.get(`/companies/${companyId}/members`)
  const data = res.data?.data ?? res.data
  const items = data?.items ?? data ?? []
  return Array.isArray(items) ? items.map(normalizeMember) : []
}
